import { Dialog } from "@headlessui/react"
import "../../styles/Admin.css"
import React, { FC, useEffect, useState } from "react"
import { X } from "heroicons-react"
import { DealOffer, User, UserAdvance, UserExpense, UserExpenseForm } from "../../../../../types"
import { KButton, KInput, KSpan, KTextArea, KTitleSpan } from "kahuna-base-react-components"
import { users } from "../../../../../apis"
import { errorFilledToastOptions, NOTIFICATION_TYPES, successFilledToastOptions } from "../../../../../constants"
import { toast } from "react-toastify"
import AdminPaymentMethodModal from "./AdminPaymentMethodModal"
import AdminUserExpenseModal from "./AdminUserExpenseModal"
import AdminUserContractModal from "./AdminUserContractModal"
import AdminUserAdvanceModal from "./AdminUserAdvanceModal"
import {
  calculateContractEnds,
  formatNumber,
  getCurrency,
  mapListToCamelCase,
  mapToCamelCase
} from "../../../../../utility"
import AdminCurrencySelectionModal from "./AdminCurrencySelectionModal"

type AdminAgreementsPayoutModalProps = {
  open: boolean
  onClose: () => void
  user: User
  loggedInUser: User
  paymentMethodsAdminEntry?: (userId?: number) => void
  onUserExpenseFormSubmit: (userExpenseForm: UserExpenseForm) => Promise<void>
  selectedUserExpense: UserExpense
  isModal: boolean
  userHasUpdateCount: number
  updateUserHasUpdateCount: (userHasUpdateCount: number) => void
}
const AdminAgreementsPayoutModal: FC<AdminAgreementsPayoutModalProps> = (props) => {
  const { onClose, open } = props

  const [confirmedOfferId, setConfirmedOfferId] = useState("")
  const [loading, setLoading] = useState(false)
  const [dealOffer, setDealOffer] = useState<DealOffer>()
  const [openCurrencySelection, setOpenCurrencySelection] = useState<boolean>(false)
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<number | undefined>(undefined)
  const [advanceTotal, setAdvanceTotal] = useState<number>(0)
  const [updateReviewOpen, setUpdateReviewOpen] = useState<boolean>(props.user.hasUpdate)

  useEffect(() => {
    if (props.open && props.user && props.user.userTakedown && props.user.userTakedown.confirmedOfferId) {
      setConfirmedOfferId(props.user.userTakedown.confirmedOfferId.toString())
      users(`/get_deal_offer_detail/?confirmed_offer_id=${props.user.userTakedown.confirmedOfferId}`, "get")
        .then((resp) => {
          if (resp && resp.status === 200 && resp.data) {
            setDealOffer(mapToCamelCase(resp.data))

            users(`/fetch_user_advance/?user_id=${props.user.id}`, "GET").then((resp) => {
              const advanceList: UserAdvance[] = mapListToCamelCase(resp.data.results)
              const advanceTotal = advanceList.reduce((total, advance) => total + (advance.advanceTotal || 0), 0)
              setAdvanceTotal(advanceTotal)
            })
          }
        })
        .catch((err) => {
          toast.error("Error fetching deal offer details.", errorFilledToastOptions)
        })
    }
  }, [props.user, props.open])

  const onCloseModal = () => {
    setConfirmedOfferId("")
    setDealOffer(undefined)
    onClose()
  }

  const onClearHasUpdate = () => {
    if (!props.user.id) return

    setLoading(true)
    users("/clear_user_has_update/", "post", { user_id: props.user.id }).then((resp) => {
      setLoading(false)
      if (resp && resp.status === 200) {
        toast.success("Marked as reviewed. Refresh the page to see result.", successFilledToastOptions)
        props.updateUserHasUpdateCount(props.userHasUpdateCount - 1)
        setUpdateReviewOpen(false)
      } else {
        toast.error("Error clearing update flag.", errorFilledToastOptions)
      }
    })
  }

  const onUpdateUserTakedown = () => {
    if (!props.user.id) return

    setLoading(true)
    users("/update_user_takedown/", "put", { user_id: props.user.id, confirmed_offer_id: confirmedOfferId }).then(
      (resp) => {
        setLoading(false)
        if (resp && resp.status === 200) {
          toast.success(
            "User Takedown updated successfully. Refresh the page to see result.",
            successFilledToastOptions
          )
        } else {
          toast.error("Error saving User Takedown.", errorFilledToastOptions)
        }
      }
    )
  }

  const renderUserTakedown = () => {
    return (
      <div>
        <KSpan text="User Takedown" fontWeight={500} />
        <div className="flex gap-2 bg-white elev-b-sm rounded-lg overflow-hidden p-6">
          <div className="flex gap-2 items-center">
            <KSpan text="Confirmed Offer ID (Scouting)" />
            <KInput
              placeholder="Enter to see deal"
              value={confirmedOfferId}
              onChange={(value) => setConfirmedOfferId(value)}
            />
          </div>
          <div className="flex">
            <KButton
              height="36px"
              dataTestId="user-takedown-confirm-button"
              text="Save User Takedown"
              background="black"
              textColor="white"
              onClick={() => onUpdateUserTakedown()}
              disabled={loading}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderUserExpense = () => {
    return (
      <div>
        <KSpan text="User Expense" fontWeight={500} />
        <AdminUserExpenseModal
          open={true}
          onClose={() => {}}
          selectedUserExpense={props.selectedUserExpense}
          onUserExpenseFormSubmit={props.onUserExpenseFormSubmit}
          user={props.user}
          loggedInUser={props.loggedInUser}
          isModal={false}
        />
      </div>
    )
  }

  const renderPaymentMethods = () => {
    return (
      <div className="mt-4">
        <KSpan text="Payment Methods" fontWeight={500} />
        <AdminPaymentMethodModal
          open={true}
          onClose={() => {}}
          paymentMethodsAdminEntry={props.paymentMethodsAdminEntry}
          userId={props.user?.id}
          readOnly={false}
          isModal={false}
        />
      </div>
    )
  }

  const renderUserAdvance = () => {
    return (
      <div>
        <KSpan text="Advances" fontWeight={500} />
        <AdminUserAdvanceModal
          open={true}
          onClose={() => {}}
          userId={props.user?.id}
          user={props?.user}
          setSelectedCurrencyId={setSelectedCurrencyId}
          onChangeCurrencyClick={() => {
            setOpenCurrencySelection(true)
          }}
          isModal={false}
        />
      </div>
    )
  }

  const renderUserContract = () => {
    return (
      <div className="mt-4">
        <KSpan text="User Contract" fontWeight={500} />
        <AdminUserContractModal open={true} onClose={() => {}} userId={props.user?.id} isModal={false} />
      </div>
    )
  }

  const Info = ({
    label,
    value,
    onClick,
    color
  }: {
    label: string
    value: string
    onClick?: () => void
    color?: string
  }) => (
    <div className="flex flex-col gap-1">
      <KSpan text={label} color={color || "#111"} fontWeight={500} />
      <div
        className="flex"
        onClick={onClick}
        style={{
          cursor: onClick ? "pointer" : "auto"
        }}
      >
        <KSpan text={value ?? "-"} color={color || "#737373"} />
      </div>
    </div>
  )

  const renderMissmatchValues = () => {
    if (props.open && props.user && props.user.userTakedown && props.user.userTakedown.confirmedOfferId && dealOffer) {
      const mismatches = []
      const offerAmount = dealOffer?.offer

      if (!dealOffer.confirmedOffer?.spotifyArtistAccount?.distributionOnly && offerAmount !== advanceTotal) {
        mismatches.push(" - Offer Amount and Advance Total mismatch!")
      }

      if ((dealOffer.newReleaseCommissionRate || 0) !== (props.selectedUserExpense.distributionFee || 0)) {
        mismatches.push(" - New Release Commission and User Expense Distribution Fee mismatch!")
      }

      if (
        (dealOffer.newReleasePostCommissionRate || 0) !== (props.selectedUserExpense.distributionFeePostRecoup || 0)
      ) {
        mismatches.push(" - New Release Post Commission and User Expense Distribution Fee Post Recoup mismatch!")
      }

      if ((dealOffer.contractPreRecoupCommission || 0) !== (props.selectedUserExpense.preRecoupCommission || 0)) {
        mismatches.push(" - Contract Pre-Recoup Commission and User Expense Pre-Recoup Commission mismatch!")
      }

      if ((dealOffer.contractPostRecoupCommission || 0) !== (props.selectedUserExpense.postRecoupCommission || 0)) {
        mismatches.push(" - Contract Post-Recoup Commission and User Expense Post-Recoup Commission mismatch!")
      }

      if (mismatches.length === 0) {
        return <div />
      }

      return (
        <div className="mb-4 flex flex-col gap-3">
          <KSpan text="WARNING!!!" color="red" fontWeight={600} fontSize={24} />
          {mismatches.map((mismatch, index) => (
            <KSpan key={index} text={mismatch} color="red" fontWeight={600} fontSize={18} />
          ))}
        </div>
      )
    }
    return <div />
  }

  const renderHasUpdateReview = () => {
    if (!updateReviewOpen) return <div />
    return (
      <div className="mb-4 w-fit">
        <KButton text="Updates Reviewed" onClick={onClearHasUpdate} />
      </div>
    )
  }

  const renderDealDetails = () => {
    const offerAmount = dealOffer?.offer
    const scoutAdvanceCommissionRate = dealOffer?.assignedScout?.affiliateDealTerms?.advanceCommissionRate
    const scoutPayout =
      offerAmount && scoutAdvanceCommissionRate ? (offerAmount * scoutAdvanceCommissionRate) / 100 : undefined
    return (
      <div className="mt-4">
        <KSpan text="Deal Details" fontWeight={500} />
        {!dealOffer ? (
          <div className="bg-white elev-b-sm rounded-lg overflow-hidden p-6">
            <KSpan text="No Deal Offer. Update confirmed offer ID and refresh page" />
          </div>
        ) : (
          <div className="bg-white elev-b-sm rounded-lg overflow-hidden p-6">
            <div className="pb-3 pt-3">
              <div className="grid grid-cols-2 gap-x-8 gap-y-4 w-full">
                <Info label="Status" value={`${dealOffer.status || "-"} (v${dealOffer.version})`} />
                <Info
                  label={"Offer"}
                  color={
                    !dealOffer.confirmedOffer?.spotifyArtistAccount?.distributionOnly && offerAmount !== advanceTotal
                      ? "red"
                      : ""
                  }
                  value={
                    dealOffer.confirmedOffer?.spotifyArtistAccount?.distributionOnly
                      ? "DISTRIBUTION ONLY"
                      : `${formatNumber(offerAmount?.toFixed(0) || "")} ${getCurrency(dealOffer.currency)}`
                  }
                />

                <Info label="Signed At" value={dealOffer.signedAt?.substring(0, 10) || "-"} />
                <Info label="Contract Ends" value={calculateContractEnds(dealOffer)} />
                <Info
                  label={dealOffer.contractModel === "post_recoup" ? "Post Recoup Term Length" : "Fixed Term Length"}
                  value={
                    (dealOffer.contractModel === "post_recoup"
                      ? dealOffer.postRecoupDuration
                      : dealOffer.contractDuration) || "-"
                  }
                />

                <Info label="Type" value={dealOffer.type || "-"} />

                <Info label="Included in Recoupment:" value={`${dealOffer.newReleaseIncludeRecoup}`} />

                <Info
                  label="New Release Pre / Post Commission %"
                  value={`${dealOffer.newReleaseCommissionRate || "-"} / ${
                    dealOffer.newReleasePostCommissionRate || "-"
                  }`}
                  color={
                    (dealOffer.newReleaseCommissionRate || 0) !== (props.selectedUserExpense.distributionFee || 0) ||
                    (dealOffer.newReleasePostCommissionRate || 0) !==
                      (props.selectedUserExpense.distributionFeePostRecoup || 0)
                      ? "red"
                      : ""
                  }
                />
                <Info
                  label="Advance Pre / Post Commission %"
                  value={`${dealOffer.contractPreRecoupCommission || "-"} / ${
                    dealOffer.contractPostRecoupCommission || "-"
                  }`}
                  color={
                    (dealOffer.contractPreRecoupCommission || 0) !==
                      (props.selectedUserExpense.preRecoupCommission || 0) ||
                    (dealOffer.contractPostRecoupCommission || 0) !==
                      (props.selectedUserExpense.postRecoupCommission || 0)
                      ? "red"
                      : ""
                  }
                />

                <Info label="Exclusivity" value={`${dealOffer.contractExclusivity}`} />

                <Info label="Recoup Base" value={dealOffer.recoupBase || "-"} />
                <Info label="Catalog Definition" value={dealOffer.catalogDefinition || "-"} />

                <Info label="Sync Rights" value={`${dealOffer.syncRights}`} />
                <Info label="Payout Frequency" value={dealOffer.payoutFrequency || "-"} />

                <Info label="Payout Threshold" value={dealOffer.payoutThreshold || "-"} />
                <Info label="Payout Currency" value={dealOffer.payoutCurrency || "-"} />

                <Info
                  label="Assigned Scout"
                  value={dealOffer.assignedScout?.name || dealOffer.assignedScout?.identifier || "-"}
                  onClick={
                    dealOffer?.assignedScoutId
                      ? () => window.open(`https://scouting.kahuna.io/scout?id=${dealOffer?.assignedScoutId}`, "_blank")
                      : undefined
                  }
                />
                <Info label="Scout Advance Commission %" value={scoutAdvanceCommissionRate?.toString() || "-"} />
                <Info
                  label="Scout Payout"
                  value={
                    dealOffer?.type === "advance" && scoutPayout !== undefined
                      ? `${formatNumber(scoutPayout || "")} ${getCurrency(dealOffer.currency)}`
                      : "-"
                  }
                />
                <Info label="Target Recoup Months" value={dealOffer?.targetRecoupMonths?.toString() || "-"} />
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderDetail = () => {
    return (
      <div>
        <div className={`flex justify-end pr-3`}>
          <X className="h-4 cursor-pointer mt-2" style={{ position: "absolute" }} onClick={() => onCloseModal()} />
        </div>
        <div
          className="bg-white elev-b-sm rounded-lg flex flex-col gap-4 custom-scroll"
          style={{ overflowY: "auto", padding: 42, maxHeight: props.isModal ? "98vh" : "92vh" }}
        >
          <div className="flex justify-between items-center">
            <KTitleSpan text={"Agreements and Payouts"} fontSize={24} lineHeight="26px" />
            <div className="flex flex-row gap-1 items-center">
              <KSpan text="(Selected User:" />
              <KSpan text={`${props.user?.firstName} ${props.user?.lastName})`} fontWeight={500} />
            </div>
          </div>

          <div>
            <div className="flex gap-8 mt-2">
              <div>
                {renderUserExpense()}
                {renderUserContract()}
                {renderPaymentMethods()}
              </div>
              <div>
                {renderMissmatchValues()}
                {renderHasUpdateReview()}
                {renderUserTakedown()}
                {renderDealDetails()}
              </div>
            </div>
            <div className="mt-4">{renderUserAdvance()}</div>
          </div>
        </div>
      </div>
    )
  }

  return props.isModal ? (
    <Dialog open={open} onClose={() => onCloseModal()} className="relative">
      <div className="fixed inset-0 bg-[rgba(0,0,0,0.3)]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {renderDetail()}
        <AdminCurrencySelectionModal
          open={openCurrencySelection}
          userId={props.user?.id}
          onClose={() => setOpenCurrencySelection(false)}
          user={props?.user}
          selectedCurrencyId={selectedCurrencyId}
        />
      </div>
    </Dialog>
  ) : (
    renderDetail()
  )
}

export default AdminAgreementsPayoutModal
